import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface KYCSettings {
  kycWarningEnabled: boolean;
  requireKYCForDeposit: boolean;
  requireKYCForWithdraw: boolean;
  requireKYCForTransfer: boolean;
}

interface UserKYCStatus {
  kycStatus: string | null;
  isVerified: boolean;
}

export const useKYCCheck = () => {
  const [settings, setSettings] = useState<KYCSettings>({
    kycWarningEnabled: true,
    requireKYCForDeposit: true,
    requireKYCForWithdraw: true,
    requireKYCForTransfer: true,
  });
  const [userKYC, setUserKYC] = useState<UserKYCStatus>({
    kycStatus: null,
    isVerified: false,
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch KYC settings from system_settings
  const fetchKYCSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("system_settings")
        .select("key, value")
        .in("key", [
          "kyc_warning_enabled",
          "require_kyc_for_deposit",
          "require_kyc_for_withdraw",
          "require_kyc_for_transfer"
        ]);

      if (error) throw error;

      console.log("🔍 Fetched KYC settings:", data);

      if (data) {
        const settingsMap = data.reduce((acc, item) => {
          acc[item.key] = item.value === "true";
          return acc;
        }, {} as Record<string, boolean>);

        const newSettings = {
          kycWarningEnabled: settingsMap.kyc_warning_enabled ?? true,
          requireKYCForDeposit: settingsMap.require_kyc_for_deposit ?? true,
          requireKYCForWithdraw: settingsMap.require_kyc_for_withdraw ?? true,
          requireKYCForTransfer: settingsMap.require_kyc_for_transfer ?? true,
        };

        console.log("🔍 Processed KYC settings:", newSettings);
        setSettings(newSettings);
      }
    } catch (error) {
      // Only log in development to keep console clean during audits
      if (import.meta.env.DEV) {
        console.error("Error fetching KYC settings:", error);
      }
    }
  };

  // Fetch current user's KYC status
  const fetchUserKYCStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("users")
        .select("kyc_status")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      console.log("🔍 Fetched user KYC data:", data);

      if (data) {
        setUserKYC({
          kycStatus: data.kyc_status,
          isVerified: data.kyc_status === "verified",
        });
      } else {
        // ถ้าไม่มีข้อมูล user หรือ kyc_status เป็น null
        setUserKYC({
          kycStatus: null,
          isVerified: false,
        });
      }
    } catch (error) {
      // Only log in development to keep console clean during audits
      if (import.meta.env.DEV) {
        console.error("Error fetching user KYC status:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchKYCSettings(), fetchUserKYCStatus()]);
    
    // Set up real-time listener for settings changes
    const channel = supabase
      .channel('kyc-settings-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings',
          filter: 'key=in.(kyc_warning_enabled,require_kyc_for_deposit,require_kyc_for_withdraw,require_kyc_for_transfer)'
        },
        (payload) => {
          console.log('🔄 KYC settings changed via real-time, refreshing...', payload);
          fetchKYCSettings();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Check if action is allowed based on KYC status and settings
  const checkKYCForAction = (action: "deposit" | "withdraw" | "transfer"): boolean => {
    console.log('🔍 KYC Check - checkKYCForAction called:', {
      action,
      settings,
      userKYC,
      kycWarningEnabled: settings.kycWarningEnabled
    });
    
    if (!settings.kycWarningEnabled) {
      console.log('🔍 KYC Check - KYC warning disabled, allowing action');
      return true;
    }

    const requirementMap = {
      deposit: settings.requireKYCForDeposit,
      withdraw: settings.requireKYCForWithdraw,
      transfer: settings.requireKYCForTransfer,
    };

    const requiresKYC = requirementMap[action];
    
    console.log('🔍 KYC Check - requirement check:', {
      action,
      requiresKYC,
      userKYCStatus: userKYC.kycStatus,
      isVerified: userKYC.isVerified
    });
    
    // If KYC is not required for this action, allow it
    if (!requiresKYC) {
      console.log('🔍 KYC Check - KYC not required for this action, allowing');
      return true;
    }

    // If KYC is required, check if user is verified
    // Block if KYC is null, pending, rejected, or incomplete
    const isAllowed = userKYC.kycStatus === "verified";
    console.log('🔍 KYC Check - final result:', {
      isAllowed,
      userKYCStatus: userKYC.kycStatus
    });
    
    return isAllowed;
  };

  // Show KYC warning for specific actions
  const shouldShowKYCWarning = (action: "deposit" | "withdraw" | "transfer"): boolean => {
    console.log("🔍 KYC Check Debug:", {
      action,
      kycWarningEnabled: settings.kycWarningEnabled,
      userKYCStatus: userKYC.kycStatus,
      checkResult: checkKYCForAction(action),
      shouldShow: settings.kycWarningEnabled && !checkKYCForAction(action)
    });
    return settings.kycWarningEnabled && !checkKYCForAction(action);
  };

  return {
    settings,
    userKYC,
    loading,
    checkKYCForAction,
    shouldShowKYCWarning,
    refreshKYCStatus: fetchUserKYCStatus,
    refreshSettings: fetchKYCSettings,
  };
};